import { Component, inject } from '@angular/core';
import { settingsPagePreviousUrl } from './constants';
import { PipelineSettingsService } from './pipeline-settings.service';
import { firstValueFrom, map, take } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ActivatedRoute, Router, RouterOutlet } from '@angular/router';
import { CrmInjectionToken } from '@galaxy/crm/static';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { StageData, StageMap } from './interfaces';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { MatButton } from '@angular/material/button';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyPageNavModule } from '@vendasta/galaxy/page-nav';
import { GalaxyNavLayoutModule } from '@vendasta/galaxy/nav-layout';
import { NgFor, AsyncPipe } from '@angular/common';
import { GalaxyNavItemComponent } from '@vendasta/galaxy/nav';
import { GalaxyEmptyStateModule } from '@vendasta/galaxy/empty-state';

@Component({
  selector: 'crm-pipeline-settings',
  templateUrl: './pipeline-settings.component.html',
  styleUrl: './pipeline-settings.component.scss',
  imports: [
    GalaxyPageModule,
    MatButton,
    GalaxyLoadingSpinnerModule,
    GalaxyPageNavModule,
    GalaxyNavLayoutModule,
    NgFor,
    GalaxyNavItemComponent,
    RouterOutlet,
    GalaxyEmptyStateModule,
    AsyncPipe,
    TranslateModule,
  ],
})
export class PipelineSettingsComponent {
  protected readonly routePrefix$ = inject(CrmInjectionToken).routePrefix$;
  protected readonly pipelines$ = this.pipelineSettingsService.allPipelines$;
  protected readonly pipelinesLoading$ = this.pipelineSettingsService.allPipelinesLoading$;
  protected readonly previousPageUrl$ = this.routePrefix$.pipe(map((prefix) => `${prefix}/${settingsPagePreviousUrl}`));

  constructor(
    private readonly pipelineSettingsService: PipelineSettingsService,
    private readonly snackbarService: SnackbarService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly translateService: TranslateService,
  ) {
    this.route.queryParamMap.pipe(take(1)).subscribe((queryParams) => {
      if (queryParams.get('create') === 'true') {
        this.createPipeline();
      }
    });
  }

  protected async createPipeline(): Promise<void> {
    const translations = await firstValueFrom(
      this.translateService.get([
        'PIPELINES.SETTINGS.DEFAULTS.NAME',
        'PIPELINES.SETTINGS.DEFAULTS.STAGE_ONE',
        'PIPELINES.SETTINGS.DEFAULTS.STAGE_TWO',
        'PIPELINES.SETTINGS.DEFAULTS.STAGE_THREE',
        'PIPELINES.SETTINGS.DEFAULTS.STAGE_FOUR',
        'PIPELINES.SETTINGS.DEFAULTS.CLOSED_WON',
        'PIPELINES.SETTINGS.DEFAULTS.CLOSED_LOST',
      ]),
    );

    const name = translations['PIPELINES.SETTINGS.DEFAULTS.NAME'];
    const stages: StageMap = new Map<string, StageData>([
      [translations['PIPELINES.SETTINGS.DEFAULTS.STAGE_ONE'], { probability: 0.2, status: 'Open' }],
      [translations['PIPELINES.SETTINGS.DEFAULTS.STAGE_TWO'], { probability: 0.3, status: 'Open' }],
      [translations['PIPELINES.SETTINGS.DEFAULTS.STAGE_THREE'], { probability: 0.5, status: 'Open' }],
      [translations['PIPELINES.SETTINGS.DEFAULTS.STAGE_FOUR'], { probability: 0.8, status: 'Open' }],
      [translations['PIPELINES.SETTINGS.DEFAULTS.CLOSED_WON'], { probability: 1, status: 'Closed Won' }],
      [translations['PIPELINES.SETTINGS.DEFAULTS.CLOSED_LOST'], { probability: 0, status: 'Closed Lost' }],
    ]);

    const newPipelineId = await firstValueFrom(this.pipelineSettingsService.createPipeline$(name, stages));
    if (newPipelineId) {
      await this.loadPipeline(newPipelineId);
      this.snackbarService.openSuccessSnack('PIPELINES.SETTINGS.SNACKBARS.CREATE_SUCCESS');
    } else {
      this.snackbarService.openErrorSnack('PIPELINES.SETTINGS.SNACKBARS.CREATE_FAILED');
    }
  }

  private async loadPipeline(pipelineId: string): Promise<void> {
    this.router.navigate([`${pipelineId}`], {
      relativeTo: this.route,
    });
  }
}
